import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

interface IAnimatedTooltipProps {
  children: React.ReactNode;
  content: string;
  trigger?: "hover" | "click";
  position?: IAnimatedTooltipPosition;
}

export type IAnimatedTooltipPosition = "top" | "bottom" | "left" | "right";

const AnimatedTooltip = ({
  children,
  content,
  trigger = "hover",
  position = "top",
}: IAnimatedTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(
    (rect: DOMRect) => {
      switch (position) {
        case "top":
          return {
            top: rect.top - 8,
            left: rect.left + rect.width / 2,
          };
        case "bottom":
          return {
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
          };
        case "left":
          return {
            top: rect.top + rect.height / 2,
            left: rect.left - 8,
          };
        case "right":
          return {
            top: rect.top + rect.height / 2,
            left: rect.right + 8,
          };
        default:
          return {
            top: rect.top - 8,
            left: rect.left + rect.width / 2,
          };
      }
    },
    [position],
  );

  useEffect(() => {
    if (isVisible && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newPosition = calculatePosition(rect);
      setTooltipPosition(newPosition);
    }
  }, [isVisible, position, calculatePosition]);

  useEffect(() => {
    if (trigger === "click" && isVisible) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsVisible(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [trigger, isVisible]);

  const handleMouseEnter = () => {
    if (trigger === "hover") {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === "hover") {
      setIsVisible(false);
    }
  };

  const handleClick = () => {
    if (trigger === "click") {
      setIsVisible(!isVisible);
    }
  };

  return (
    <>
      <TooltipContainer
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: trigger === "click" ? "pointer" : "default" }}
      >
        {children}
      </TooltipContainer>
      {isVisible &&
        createPortal(
          <TooltipContent
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
            }}
            onMouseEnter={trigger === "hover" ? handleMouseEnter : undefined}
            onMouseLeave={trigger === "hover" ? handleMouseLeave : undefined}
            $allowPointerEvents={trigger === "click"}
            $position={position}
          >
            {content}
          </TooltipContent>,
          document.body,
        )}
    </>
  );
};

const fadeInTop = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -100%) translateY(4px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -100%) translateY(0);
  }
`;

const fadeInBottom = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 0%) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0%) translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translate(-100%, -50%) translateX(4px);
  }
  to {
    opacity: 1;
    transform: translate(-100%, -50%) translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translate(0%, -50%) translateX(-4px);
  }
  to {
    opacity: 1;
    transform: translate(0%, -50%) translateX(0);
  }
`;

const TooltipContainer = styled.div`
  display: inline-block;
`;

const TooltipContent = styled.div<{
  $allowPointerEvents?: boolean;
  $position: "top" | "bottom" | "left" | "right";
}>`
  position: fixed;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.nedAlpha50};
  color: ${({ theme }) => theme.colors.ice};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  pointer-events: ${({ $allowPointerEvents }) =>
    $allowPointerEvents ? "auto" : "none"};
  opacity: 0;
  animation: ${({ $position }) => {
      switch ($position) {
        case "top":
          return fadeInTop;
        case "bottom":
          return fadeInBottom;
        case "left":
          return fadeInLeft;
        case "right":
          return fadeInRight;
        default:
          return fadeInTop;
      }
    }}
    0.2s ease-in-out forwards;
`;

export { AnimatedTooltip };
